import { useRef, useEffect, useState } from "react";
import type { FC } from "react";
import { accordionStyles } from "@/design-system/Molecules/AccordionList/Accordion.styles";
import { Button } from "@/design-system/Atoms";
import type {
  WatchedVideo,
  Video,
} from "@/rtkQuery/features/models/video.model";
import VideoPlayerModal from "@/design-system/Molecules/VideoPlayerModal";
import videoThumbnailMap, {
  DEFAULT_THUMBNAIL,
} from "@/constants/videoThumbnailMap";

interface VideosAccordionItemProps {
  video: WatchedVideo;
  isOpen: boolean;
  onToggle: () => void;
}

export const VideosAccordionItem: FC<VideosAccordionItemProps> = ({
  video,
  isOpen,
  onToggle,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState("0px");
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);

  const getThumbnail = (): string => {
    const title =
      video.requiredVideo?.videoName ||
      video.requiredVideo?.fileName.replace(/\.[^/.]+$/, "");
    const thumbnail = video.requiredVideo?.thumbnail ?? videoThumbnailMap[title];
    return `/images/video-thumbnail/${thumbnail || DEFAULT_THUMBNAIL}`;
  };

  useEffect(() => {
    if (isOpen && contentRef.current) {
      setMaxHeight(`${contentRef.current.scrollHeight}px`);
    } else {
      setMaxHeight("0px");
    }
  }, [isOpen]);

  const handleOpenModal = (video: Video) => {
    setSelectedVideo(video);
    setIsVideoModalOpen(true);
  };

  const closeVideoModal = () => {
    setIsVideoModalOpen(false);
    setSelectedVideo(null);
  };

  return (
    <>
      <div className={`${accordionStyles.item}`}>
        <h2
          className={accordionStyles.header(isOpen)}
          onClick={onToggle}
          aria-expanded={isOpen}
          role="button"
          tabIndex={0}
          id={`${video.requiredVideo?.id}`}
          aria-controls={`panel-${video.requiredVideo?.id}`}
          onKeyDown={(e) => e.key === "Enter" && onToggle()}
        >
          <button
            type="button"
            className={accordionStyles.buttonIcon}
            aria-label="Toggle Accordion"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
          >
            <span
              className={`accordion-arrow-icon-wrapper ${isOpen ? "rotate" : ""}`}
            >
              <span className="accordion-arrow-icon" />
            </span>
          </button>
          <span className="truncate block w-full">
            {video.requiredVideo?.videoName ||
              video.requiredVideo?.fileName.replace(/\.[^/.]+$/, "")}
          </span>
        </h2>

        <div
          ref={contentRef}
          className="overflow-hidden transition-all duration-500 ease-in-out"
          style={{ maxHeight }}
          id={`panel-${video.requiredVideo?.id}`}
          aria-labelledby={`${video.requiredVideo?.id}`}
        >
          <div className={accordionStyles.panel}>
            <div
              className="relative w-fit cursor-pointer group"
              onClick={() =>
                handleOpenModal({
                  Id: video.requiredVideo.id,
                  ProductId: video.requiredVideo.productId,
                  SortOrder: video.requiredVideo.sortOrder,
                  Url: video.requiredVideo.url,
                  VideoName: video.requiredVideo.videoName,
                  FileName: video.requiredVideo.fileName,
                  Thumbnail: video.requiredVideo.thumbnail,
                  IsHidden: video.requiredVideo.isHidden,
                  RecordCreatedDateTime: new Date().toISOString(),
                  RecordModifiedDateTime: null,
                  IsRemoved: 0,
                  RemovedAt: null,
                  WasWatched: video.wasWatched,
                })
              }
            >
              <img
                src={getThumbnail()}
                alt={video.requiredVideo?.fileName}
                className="w-auto h-48 object-cover rounded"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="rounded-full p-3 transition-opacity duration-200 group-hover:opacity-100">
                  <i className="bi bi-play-fill text-gray-400 text-6xl"></i>
                </div>
              </div>
            </div>
            <div className="mt-6 mb-0.5">
              <Button
                variant="graySubmitButton"
                size="xl"
                type="button"
                onClick={() =>
                  handleOpenModal({
                    Id: video.requiredVideo.id,
                    ProductId: video.requiredVideo.productId,
                    SortOrder: video.requiredVideo.sortOrder,
                    Url: video.requiredVideo.url,
                    VideoName: video.requiredVideo.videoName,
                    FileName: video.requiredVideo.fileName,
                    Thumbnail: video.requiredVideo.thumbnail,
                    IsHidden: video.requiredVideo.isHidden,
                    RecordCreatedDateTime: new Date().toISOString(),
                    RecordModifiedDateTime: null,
                    IsRemoved: 0,
                    RemovedAt: null,
                    WasWatched: video.wasWatched,
                  })
                }
                className="w-full sm:w-[11.7rem] md:w-[22.7rem] lg:w-[22.7rem] xl:w-[22.7rem] 2xl:w-[22.7rem]"
              >
                View Video
              </Button>
            </div>
          </div>
        </div>
      </div>

      {selectedVideo && (
        <VideoPlayerModal
          isOpen={isVideoModalOpen}
          onClose={closeVideoModal}
          content={selectedVideo}
        />
      )}
    </>
  );
};
